import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Zap, Lock } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

const API = '/api';

export default function AdminLoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const ownerAppMode = searchParams.get('app') === 'owner';
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Retry login up to 2 times for cold-start resilience
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        const { data } = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true, timeout: 10000 });
        if (data.role === 'admin') {
          localStorage.setItem('admin_token', data.access_token);
          toast.success('Welcome back!');
          // Preserve PWA owner-mode flag through the login round-trip.
          navigate(ownerAppMode ? '/admin?app=owner' : '/admin');
          return;
        } else {
          setError('Admin access required');
          setLoading(false);
          return;
        }
      } catch (err) {
        if (attempt < 2 && (!err.response || err.response.status >= 500 || err.code === 'ECONNABORTED')) {
          // Server starting up — wait and retry
          await new Promise(r => setTimeout(r, 2000));
          continue;
        }
        const detail = err.response?.data?.detail;
        if (typeof detail === 'string') {
          setError(detail);
        } else if (Array.isArray(detail)) {
          setError(detail.map(e => e.msg || JSON.stringify(e)).join(' '));
        } else if (!err.response) {
          setError('Server is starting up. Please wait a moment and try again.');
        } else {
          setError('Login failed. Please try again.');
        }
      }
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-6" data-testid="admin-login-page">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Zap className="w-8 h-8 text-primary" />
            <span className="text-2xl font-light text-primary" style={{ fontFamily: 'Outfit' }}>Powersure Admin</span>
          </div>
          <p className="text-muted-foreground">Sign in to access the admin dashboard</p>
        </div>

        <form onSubmit={handleLogin} className="bg-white rounded-xl p-8 border border-border shadow-sm">
          <div className="space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email" type="email" value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@powersure.com"
                required data-testid="admin-email-input"
              />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password" type="password" value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                required data-testid="admin-password-input"
              />
            </div>
            {error && <p className="text-sm text-destructive" data-testid="login-error">{error}</p>}
            <button
              type="submit" disabled={loading}
              className="w-full btn-primary py-3 rounded-md font-medium flex items-center justify-center gap-2"
              data-testid="admin-login-btn"
            >
              <Lock className="w-4 h-4" />
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
            <div className="text-center pt-2">
              <Link
                to="/forgot-password"
                className="text-sm text-primary hover:underline"
                data-testid="admin-forgot-password-link"
              >
                Wachtwoord vergeten?
              </Link>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
